import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, MapPin, Users, CheckCircle2, XCircle } from "lucide-react";

interface ReviewSummaryTabProps {
  data: any;
}

// Helper to convert 24-hour time to 12-hour AM/PM format
const formatTime12Hour = (time24: string | null | undefined): string => {
  if (!time24) return '';
  try {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch {
    return time24;
  }
};

export function ReviewSummaryTab({ data }: ReviewSummaryTabProps) {
  return (
    <div className="space-y-6">
      <Card className="p-6 space-y-4">
        <div>
          <h3 className="text-xl font-semibold mb-4">Meeting Overview</h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Meeting Type & Date</p>
                  <p className="font-medium">{data.meeting_type}</p>
                  <p className="text-sm">{new Date(data.meeting_date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Duration</p>
                  <p className="font-medium">
                    {formatTime12Hour(data.meeting_start_time)} - {data.meeting_end_time ? formatTime12Hour(data.meeting_end_time) : "Not concluded"}
                  </p>
                </div>
              </div>

              {data.meeting_location && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="font-medium">{data.meeting_location}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Users className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Recording Secretary</p>
                  <p className="font-medium">
                    {data.recording_secretary?.full_name || "Not specified"}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Users className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Presiding Officer</p>
                  <p className="font-medium">
                    {data.presiding_officer?.full_name || "Not specified"}
                  </p>
                </div>
              </div>

              {data.quorum_required && (
                <div className="flex items-start gap-3">
                  {data.quorum_met ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Quorum Status</p>
                    <p className="font-medium">
                      {data.actual_attendees.length} / {data.quorum_threshold} required
                    </p>
                    <Badge variant={data.quorum_met ? "default" : "destructive"}>
                      {data.quorum_met ? "Quorum Met" : "Quorum Not Met"}
                    </Badge>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {data.meeting_purpose && (
        <Card className="p-6">
          <h3 className="font-semibold mb-3">Meeting Purpose</h3>
          <p className="text-muted-foreground whitespace-pre-wrap">{data.meeting_purpose}</p>
        </Card>
      )}

      {/* Meeting Statistics */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4">Meeting Statistics</h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-muted rounded-lg">
            <p className="text-3xl font-bold text-primary">{data.actual_attendees.length}</p>
            <p className="text-sm text-muted-foreground mt-1">Members Present</p>
          </div>
          
          <div className="text-center p-4 bg-muted rounded-lg">
            <p className="text-3xl font-bold text-primary">{data.agenda_items?.length || 0}</p>
            <p className="text-sm text-muted-foreground mt-1">Agenda Items</p>
          </div>
          
          <div className="text-center p-4 bg-muted rounded-lg">
            <p className="text-3xl font-bold text-primary">{data.motions?.length || 0}</p>
            <p className="text-sm text-muted-foreground mt-1">Motions Voted</p>
          </div>
          
          <div className="text-center p-4 bg-muted rounded-lg">
            <p className="text-3xl font-bold text-primary">{data.action_items?.length || 0}</p>
            <p className="text-sm text-muted-foreground mt-1">Action Items</p>
          </div>
        </div>
      </Card>

      {data.general_notes && (
        <Card className="p-6">
          <h3 className="font-semibold mb-3">General Notes</h3>
          <p className="text-muted-foreground whitespace-pre-wrap">{data.general_notes}</p>
        </Card>
      )}
    </div>
  );
}
